import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { Diagnosis } from 'src/app/models/fhir-models';
import { AcuityLevel, DeescalationFlag, EscalationFlag } from './acuity.model';
import { User } from './user.model';

export interface Patient extends User {
  client_ids?: string[];
  active_rpm?: boolean;
  addresses?: PatientAddress[];
  alert_contacts?: AlertContact[];
  contact_consent?: ContactConsent;
  contacts?: PatientContact[];
  coverages?: InsuranceCoverage[];
  compliance?: Compliance;
  diagnoses?: Diagnosis[];
  language?: string;
  dob?: any;
  emr_id?: string;
  emr_data?: PatientEmrData;
  height_inches?: number;
  last_alert_date?: any;
  last_assessment?: any;
  last_measurement_date?: any;
  lastCall?: LastCall;
  next_follow_up?: string;
  notes?: string;
  pregnancies?: number;
  isPregnant?: boolean;
  start_last_pregnancy?: any;
  provider_id?: string;
  providers?: Provider[];
  providers_ids?: string[];
  status?: PatientStatus;
  weight_lbs?: number;
  primary_coverage_type?: CoverageTypes;
  providerGenerated?: boolean;
  lastSurveyDate?: any;
  last_awv_form?: LastAwvForm;
  id?: string;
  billingCodes?: string[];
  primaryContact?: PatientContact;
  viewers_on_chart?: ViewOnChart[];
  hubspot_id?: string;
  programs?: string[];
  department?: { id: string; name: string };
  initial_awv?: InitialAWV;
  graduationCheckDate?: any;
  graduation_pop_up_enable?: boolean;
  pes?: { firstName: string; email: string; lastName: string };
  lastContact?: { date: string; type: string };
  ccm_to_apcm_consent?: {
    consent: boolean;
    consent_date: Date | firebase.firestore.Timestamp | null;
    consent_by: string;
  };
  record_consent?: boolean;
  record_consent_date?: Date | firebase.firestore.Timestamp | firebase.firestore.FieldValue | null;
  computedAcuityLevel?: AcuityLevel;
  acuityEscalation?: EscalationFlag;
  acuityDeescalation?: DeescalationFlag;
  direct_referral?: boolean;
  direct_referral_date?: firebase.firestore.Timestamp | null;
  direct_referral_by?: string;
}

export interface InitialAWV {
  date: any;
  code: string;
  created_by: string;
  form_id?: string;
  claim_id?: string;
}
export interface BMIPatient {
  bmi: number;
  category: string;
}

export interface Compliance {
  vitalsReport?: VitalsReport;
  timeReportByPatient?: TimeReportByPatient;
}

export interface VitalsReport {
  uniqueMeasures?: number;
  totalMeasures?: number;
}

export interface TimeReportByPatient {
  user_id: string;
  rpmc: number;
  rpms: number;
  rpm: number;
  rpmTotal: number;
  pcmTotal: number;
  pinTotal: number;
  chiTotal: number;
  bhiTotal: number;
  tcm: boolean;
  ccm: number;
  pcm: number;
  pin: number;
  chi: number;
  bhi: number;
  contacted: boolean;
  ccmTotal: number;
  other: number;
  billable: number;
  times: [];
}

export interface LastAwvForm {
  date?: any;
  id?: string;
}
export interface ViewOnChart {
  name: string;
  user_id: string;
  since?: any;
  tabs_open?: number;
  initials?: string;
  tooltip?: string;
}

export enum ClinicalLicensure {
  MEDICAL_ASSISTANT = 'Medical Assistant',
  REGISTERED_NURSE = 'Registered Nurse',
  NURSE_PRACTITIONER = 'Nurse Practitioner',
  CARE_COORDINATOR = 'Care Coordinator',
  LICENSED_VOCATIONAL_NURSE = 'Licensed Vocational Nurse',
  OTHER = 'Other',
}

export interface Provider {
  uid: string;
  name: string;
  email: string;
  isAvailable: boolean;
  rank: number;
  role?: ClinicalLicensure;
  dateAdded?: any;
}

export interface LastCall {
  lastCallDate?: any;
  lastCallStatus?: string;
  lastCallSid?: string;
  lastCallDuration?: number | string;
}

export interface AlertContact {
  type?: string;
  data?: string;
}

export interface ContactConsent {
  email?: boolean;
  phone?: boolean;
  sms?: any;
}

export interface Roles {
  isPatient?: boolean;
  isClient?: boolean;
  isAdmin?: boolean;
  isWelbyClinical?: boolean;
  isPracticeAdmin?: boolean;
  isManagerNurse?: boolean;
  isMedicalAssistant?: boolean;
  canViewAllCalls?: boolean;
  /** Opens the feature flags console. Only honoured together with `isAdmin` (see FeatureFlagsAccessGuard). */
  isFeatureFlagsManager?: boolean;
}

export interface PatientStatus {
  active?: boolean;
  ccm?: boolean;
  ccm_last_update?: any;
  deceased?: boolean;
  rpm?: boolean;
  rpm_category?: string;
  pcm?: boolean;
  tcm?: boolean;
  rtm?: boolean;
  rtm_last_update?: any;
  tcm_last_update?: any;
  pcm_last_update?: any;
  rpm_last_update?: any;
  bhi?: boolean;
  bhi_last_update?: any;
  disenrollment_ccm_comments?: string;
  disenrollment_rpm_comments?: string;
  label?: string;
  apcm?: boolean;
  /**
   * Optional like every other `_last_update`, and for the same reason: charts written before the
   * program existed simply do not carry it.
   *
   * It was missing from this interface while the data and welby-micro-service's own `PatientStatus`
   * both had it — `acuity.service.ts` and `patient.service.ts` write it, and the CCM→APCM transition
   * report reads it as "APCM Start Date" — so the model claimed APCM had no timestamp when it does.
   */
  apcm_last_update?: any;
  tier_apcm?: string;
  on_hold?: { start_date: any; end_date: any };
  chart_prepped?: boolean;
  patient_activated?: boolean;
  patient_activation_attempted?: boolean;
}

export interface Message {
  id?: string;
  author_id: string;
  patient_id?: string;
  client_id?: string;
  compose_date: Date;
  text: string;
  read?: boolean;
  patient_sent?: boolean;
}

export const CONTACT_DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

export interface PatientContact {
  id?: string;
  contact_data?: string; // the info (i.e. email, number)
  contact_type?: 'home' | 'cell' | 'email' | 'fax';
  createdAt: any;
  doc?: any;
  icon_name?: string;
  isPrimary?: boolean;
  primary?: boolean;
  isPrimaryForSms?: boolean;
  updatedAt?: any;
  contact_description?: string;
  best_times_days?: string[];
  best_times_period?: ('morning' | 'afternoon')[];
}

export interface PatientAddress {
  id?: string;
  address: string;
  address1?: string;
  address2?: string;
  city: string;
  county?: string;
  state: string;
  zip: any;
  zip4?: any;
  zip5?: any;
  postalCode?: any;
  primary?: boolean;
  doc?: any;
}

export interface PatientFiles {
  id?: string;
  title: string;
  description: string;
  url: string;
  file_key?: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy: {
    id: string;
    name: string;
  };
  user_id: string;
  return_label_expiration_date?: Date | firebase.firestore.Timestamp;
  return_id?: string;
  carrier?: string;
}

export interface InsuranceCoverage {
  id?: string;
  effective_date?: Date;
  end_date?: Date;
  logo_image_url?: string;
  member_id?: string;
  payor?: string;
  plan_type?: string;
}

export interface Device {
  id?: string;
  ordered?: boolean;
  delivered?: boolean;
  active?: boolean;
  auth_token?: string;
  device_manufacturer?: string;
  device_model?: string;
  device_id?: string;
  imageURL?: string;
  last_update?: any;
  refresh_token?: string;
  user_id?: number;
  order_notes?: string;
}

export interface PatientAppointment {
  creator_id?: string;
  patient_id?: string;
  date?: string;
  startTime?: string;
  endTime?: string;
  type?: string;
  status?: AppointmentStatus;
}

export interface AppointmentStatus {
  isActive?: boolean;
  isWaiting?: boolean;
}

export interface PatientEmrData {
  mrn: string;
  emr_guid: string;
  renderingproviderid?: string;
  renderingprovidername?: string;
}

export enum CoverageTypes {
  MEDICARE = 'Medicare',
  MEDICAID = 'Medicaid',
  COMMERCIAL = 'Commercial',
  CASH = 'Cash',
  OTHER = 'Other',
}

export enum Languages {
  ENGLISH = 'English',
  SPANISH = 'Spanish',
  FRENCH = 'French',
  GERMAN = 'German',
  JAPANESE = 'Japanese',
  MANDARIN = 'Mandarin',
  PORTUGUESE = 'Portuguese',
  VIETNAMESE = 'Vietnamese',
  OTHER = 'Other',
}

export interface PatientChurn {
  patient_id: string;
  client_id: string;
  care_manager_uid: string;
  pes_rep: { firstName: string; email: string; lastName: string };
  program_start_date: any;
  program_end_date: any;
  term_reason: string;
  program: string;
}

export interface AddressValidationResponse {
  error?: string;
  secondaryAddressRequired?: boolean;
  traceId: string;
  data: {
    firm: string;
    address: {
      streetAddress: string;
      streetAddressAbbreviation: string;
      secondaryAddress: string;
      cityAbbreviation: string;
      city: string;
      state: string;
      ZIPCode: string;
      ZIPPlus4: string;
      urbanization: string;
    };
    additionalInfo: {
      deliveryPoint: string;
      carrierRoute: string;
      DPVConfirmation: string;
      DPVCMRA: string;
      business: string;
      centralDeliveryPoint: string;
      vacant: string;
    };
    corrections: {
      code: string;
      text: string;
    }[];
    matches: {
      code: string;
      text: string;
    }[];
  };
}
